import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { useDivisions } from '@/hooks/useDivisions';
import { parseList } from '../format';
import { useDnsDomains, useSetDnsDomains } from '../hooks';
import { useVpnPermissions } from '../permissions';
import { Card } from './Facts';

function ScopeRow({
  divisionId,
  label,
  saved,
  fallback,
  canEdit,
}: {
  divisionId: string | null;
  label: string;
  saved: string[];
  /** What applies when this scope has none: a division falls back to the organization's. */
  fallback?: string;
  canEdit: boolean;
}) {
  const savedText = saved.join(', ');
  const [text, setText] = useState(savedText);
  const save = useSetDnsDomains();
  const changed = parseList(text).join(', ') !== savedText;
  const inputId = `vpn-dns-${divisionId ?? 'org'}`;

  return (
    <div className="grid gap-1.5 py-3 first:pt-0 last:pb-0">
      <label htmlFor={inputId} className="text-sm font-medium">
        {label}
      </label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          value={text}
          disabled={!canEdit}
          title={canEdit ? undefined : 'Changing them takes vpn:update here'}
          placeholder={fallback ?? 'customer.internal, corp.example'}
          onChange={(e) => { setText(e.target.value); }}
        />
        <Button
          variant="outline"
          disabled={!canEdit || !changed || save.isPending}
          onClick={() => { save.mutate({ divisionId, domains: parseList(text) }); }}
        >
          Save
        </Button>
      </div>
      {saved.length === 0 && fallback ? <p className="text-xs text-muted-foreground">{fallback}</p> : null}
    </div>
  );
}

/**
 * The DNS domains split DNS sends through the tunnel, for the organization and
 * for each division (docs/VpnAccess.md, "Split DNS"). There is no division
 * settings page, so every scope is listed here.
 */
export function DnsDomainsCard() {
  const { user } = useAuth();
  const { canUpdateVpn, canUpdateOrgVpn } = useVpnPermissions();
  const lists = useDnsDomains();
  const divisions = useDivisions(user?.organizationId);
  const savedFor = (divisionId: string | null) =>
    lists.data?.find((l) => l.divisionId === divisionId)?.domains ?? [];

  return (
    <Card
      title="DNS domains"
      description="Names in these domains, and the reverse zones of a session's routes, resolve through the tunnel at the site's DNS servers. Everything else keeps the technician's own DNS."
    >
      {lists.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : lists.isError ? (
        <p className="text-xs text-muted-foreground">Couldn&rsquo;t load the DNS domains.</p>
      ) : (
        <div className="divide-y divide-border">
          {/* Keyed by what's saved, so a save or another viewer's change resets the field. */}
          <ScopeRow
            key={`org:${savedFor(null).join(',')}`}
            divisionId={null}
            label="Organization"
            saved={savedFor(null)}
            canEdit={canUpdateOrgVpn}
          />
          {(divisions.data ?? []).map((d) => (
            <ScopeRow
              key={`${d.id}:${savedFor(d.id).join(',')}`}
              divisionId={d.id}
              label={d.name}
              saved={savedFor(d.id)}
              fallback="None of its own: the organization's apply."
              canEdit={canUpdateVpn}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
